import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { classNames } from "../utils/classNames";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps {
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
}

/**
 * HerSkin 的统一单选下拉框。
 *
 * 浏览器原生 select 的展开面板由操作系统绘制，圆角、间距和选中态无法可靠
 * 纳入设计系统；这个轻量 listbox 保留键盘导航、焦点和屏幕阅读器语义，同时让
 * 箭头、浮层和选项状态在 Windows、macOS 与移动浏览器中保持一致。
 */
export function SelectField({
  value,
  options,
  onValueChange,
  ariaLabel,
  className,
  disabled = false,
  placeholder = "请选择",
  name,
}: SelectFieldProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();
  const enabled = useMemo(
    () =>
      options.map((option, index) => ({ option, index })).filter(({ option }) => !option.disabled),
    [options],
  );
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const fallback = enabled[0]?.index ?? -1;
    setActiveIndex(selected && !selected.disabled ? selectedIndex : fallback);
  }, [enabled, open, selected, selectedIndex]);

  const move = (direction: 1 | -1) => {
    if (!enabled.length) return;
    const current = enabled.findIndex(({ index }) => index === activeIndex);
    const next = current < 0 ? 0 : (current + direction + enabled.length) % enabled.length;
    setActiveIndex(enabled[next]!.index);
  };

  const commit = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    onValueChange(option.value);
    setOpen(false);
    requestAnimationFrame(() => buttonRef.current?.focus());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) setOpen(true);
      else move(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (event.key === "Home" && open) {
      event.preventDefault();
      setActiveIndex(enabled[0]?.index ?? -1);
      return;
    }
    if (event.key === "End" && open) {
      event.preventDefault();
      setActiveIndex(enabled.at(-1)?.index ?? -1);
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      commit(activeIndex);
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && !open) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div
      className={classNames("herskin-select", open && "herskin-select--open", className)}
      ref={rootRef}
    >
      {name ? <input type="hidden" name={name} value={value} /> : null}
      <button
        ref={buttonRef}
        type="button"
        className="herskin-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? undefined : "herskin-select__placeholder"}>
          {selected?.label ?? placeholder}
        </span>
        <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16">
          <path
            d="m4 6 4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <div className="herskin-select__menu" id={listboxId} role="listbox" aria-label={ariaLabel}>
          {options.map((option, index) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              disabled={option.disabled}
              className={classNames(
                "herskin-select__option",
                index === activeIndex && "herskin-select__option--active",
              )}
              key={option.value}
              onPointerMove={() => !option.disabled && setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              <span>{option.label}</span>
              {option.value === value ? (
                <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16">
                  <path
                    d="m3.5 8 3 3 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
